import { Box, Chip, TextField, Typography } from '@material-ui/core'
import { Button } from 'components/facets'
import { changeOrdersState } from 'lib/api'
import { useDebounce, useDialog, useOrderStore } from 'lib/hooks'
import { useFetchOrders } from 'lib/hooks/useFetchOrders'
import { getGameCodeFromName } from 'lib/utils'
import { memo, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  getOrderState,
  hasIssues,
  Order,
  OrderArticle,
  orderContainsSealed,
  OrderFilter,
  ORDER_FILTERS,
  ORDER_SIDEBAR_TABS,
  ORDER_TABS,
} from 'shared'
import { shallow } from 'zustand/shallow'
import { ExportOrders } from '../csv/ExportOrders'
import { ColorBox } from './ColorBox'
import { PickingListToolbar } from './PickingSection/PickingListToolbar'

export interface OrdersToolbarProps {
  handleUpdateOrders: (updatedOrders: Order[]) => void
  viewOnly?: boolean
}

export const OrdersToolbar = memo((props: OrdersToolbarProps): JSX.Element => {
  const { handleUpdateOrders, viewOnly } = props
  const { t } = useTranslation()

  const [
    orders,
    filteredOrders,
    startDate,
    activeTab,
    activeOrdersFilters,
    loadingFromMkm,
    view,
    filterByOrder,
    selectedOrders,
    setFilterByOrder,
    setFilteredOrders,
    setActiveOrdersFilters,
    setSelectedOrders,
    setSelectedOrderIds,
  ] = useOrderStore(
    (s) => [
      s.orders,
      s.filteredOrders,
      s.startDate,
      s.activeTab,
      s.activeOrdersFilters,
      s.loadingFromMkm,
      s.view,
      s.filterByOrder,
      s.selectedOrders,
      s.setFilterByOrder,
      s.setFilteredOrders,
      s.setActiveOrdersFilters,
      s.setSelectedOrders,
      s.setSelectedOrderIds,
    ],
    shallow
  )

  const { convertOrderResponseDtoToOrderClass } = useFetchOrders()

  const [setExportDialogOpen, ExportDialog] = useDialog()
  const [disabledPackedButton, setDisabledPackedButton] = useState(true)
  const [disabledSendButton, setDisabledSendButton] = useState(true)

  useEffect(() => {
    if (
      selectedOrders.length === 0 ||
      selectedOrders.some((order) => {
        return order.state.state !== 'picked'
      })
    ) {
      setDisabledPackedButton(true)
    } else {
      setDisabledPackedButton(false)
    }

    if (
      selectedOrders.length === 0 ||
      selectedOrders.some((order) => {
        return order.state.state !== 'packed'
      })
    ) {
      setDisabledSendButton(true)
    } else {
      setDisabledSendButton(false)
    }
  }, [selectedOrders.length])

  const [searchString, setSearchString] = useState('')
  const debounceTime = 200
  const debouncedSearchString = useDebounce(searchString, debounceTime)

  const openExportOrdersDialog = () => {
    setExportDialogOpen(true)
  }

  const orderContainsArticle = (order: Order, articleName: string) => {
    for (const a of order.articles ?? []) {
      if (
        a.card.name.toLowerCase().includes(articleName.toLowerCase()) ||
        a.getLocalizedName().toLowerCase().includes(articleName.toLowerCase())
      ) {
        return true
      }
    }
  }

  const orderContainsExpansion = (order: Order, expansionName: string) => {
    for (const a of order.articles ?? []) {
      if (a.card.expansion?.toLowerCase().includes(expansionName.toLowerCase())) {
        return true
      }
    }
  }

  const filterForIssues = (stateFilters: string[], order: Order) => {
    if (stateFilters.includes('issues')) {
      return hasIssues(order)
    }
    return true
  }

  const filterByGames = (activeGameFilters: string[], order: Order) => {
    if (activeGameFilters.length === 0) return true
    for (const game of order.articleGames) {
      // accessories and unrecognized articles have no game filter: they never hide an order
      if (game === 'Unknown' || game === 'Generic' || activeGameFilters.includes(game)) {
        return true
      }
    }
    return false
  }

  const filterByItemType = (stateFilters: string[], order: Order) => {
    if (stateFilters.includes('sealed')) {
      return orderContainsSealed(order)
    } else {
      return true
    }
  }

  const filterByPresale = (stateFilters: string[], order: Order) => {
    if (stateFilters.includes('presale')) {
      return order.isPresale
    } else {
      return true
    }
  }

  const orderContainsComment = (order: Order, comment: string) => {
    let filtered: OrderArticle[] = []
    if (comment.includes('"')) {
      filtered =
        order.articles?.filter((a) => a.comment.toLowerCase() === comment.replace(/"/g, '')) ?? []
    } else {
      filtered = order.articles?.filter((a) => a.comment.toLowerCase().includes(comment)) ?? []
    }
    return filtered.length > 0
  }

  const filterOrders = () => {
    if (activeTab !== -1) {
      let newFilteredOrders = [...orders]

      newFilteredOrders = newFilteredOrders.filter(
        (o) =>
          ORDER_TABS[activeTab].states.includes(o.state.state) ||
          ORDER_TABS[activeTab].states.includes('')
      )

      newFilteredOrders = newFilteredOrders.filter((o) => new Date(o.updatedAt) >= startDate)

      if (debouncedSearchString) {
        newFilteredOrders = newFilteredOrders.filter(
          (o) =>
            o.sourceOrderId.startsWith(debouncedSearchString) ||
            o.buyer?.username?.toLowerCase()?.includes(debouncedSearchString.toLowerCase()) ||
            o.shipping?.name?.toLowerCase()?.includes(debouncedSearchString.toLowerCase()) ||
            orderContainsArticle(o, debouncedSearchString) ||
            orderContainsExpansion(o, debouncedSearchString) ||
            orderContainsComment(o, debouncedSearchString)
        )
      }

      // filter by order state
      const stateFilters: string[] = []
      activeOrdersFilters.forEach((af) => {
        const filterConfig = ORDER_FILTERS.find((f) => f.state === af)
        stateFilters.push(filterConfig?.state || '')
      })
      newFilteredOrders = newFilteredOrders.filter((o) => stateFilters.includes(getOrderState(o)))

      // filter by issues
      newFilteredOrders = newFilteredOrders.filter((o) => filterForIssues(stateFilters, o))

      // filter by game
      const activeGameFilters = activeOrdersFilters.filter(
        (af) => ORDER_FILTERS.find((f) => f.state === af)?.isGame
      )
      newFilteredOrders = newFilteredOrders.filter((o) => filterByGames(activeGameFilters, o))

      // filter by items type (choose between showing order with just singles or orders
      // with singles + sealed)
      newFilteredOrders = newFilteredOrders.filter((o) => filterByItemType(stateFilters, o))

      // filter by presale or "normal"
      newFilteredOrders = newFilteredOrders.filter((o) => filterByPresale(stateFilters, o))

      // sort orders
      newFilteredOrders = getSortedOrders(newFilteredOrders)

      setFilteredOrders(newFilteredOrders)
    }
  }

  useEffect(() => {
    filterOrders()
  }, [activeTab, activeOrdersFilters, startDate, debouncedSearchString, orders])

  const sortOrdersFn = (a: Order, b: Order): number => {
    if (activeTab === 1) {
      if (a.picking && b.picking && a.picking.pickingList && b.picking.pickingList) {
        const sortingFactor = a.picking.pickingList.localeCompare(b.picking.pickingList)
        if (sortingFactor === 0) {
          return a.picking.pickingId.localeCompare(b.picking.pickingId)
        }
        return sortingFactor
      } else if (a.picking && b.picking && a.picking.pickingList && !b.picking.pickingList) {
        return -1
      } else {
        return 1
      }
    } else {
      return new Date(a.state.datePaid).getTime() < new Date(b.state.datePaid).getTime() ? 1 : -1
    }
  }

  const getSortedOrdersForPicking = (orders: Order[]) => {
    const notPicking: Order[] = []
    const picking: Order[] = []
    orders.forEach((o) => {
      const state = getOrderState(o)
      if (state === 'paid' || state === 'bought') {
        notPicking.push(o)
      } else if (state === 'picking') {
        picking.push(o)
      }
    })

    notPicking.sort((a, b) => {
      const datePaidA = new Date(a.state.datePaid)
      const datePaidB = new Date(b.state.datePaid)
      return datePaidA.getTime() - datePaidB.getTime()
    })

    const pickingWithCorrectFields = picking.filter((o) => o.picking)
    // this is for debugging in staging

    pickingWithCorrectFields.sort((a, b) => {
      const listA = a.picking?.pickingList ?? ''
      const listB = b.picking?.pickingList ?? ''

      const idA = a.picking?.pickingId ?? ''
      const idB = b.picking?.pickingId ?? ''

      return listA.localeCompare(listB) || idA.localeCompare(idB)
    })

    return [...notPicking, ...pickingWithCorrectFields]
  }

  const getSortedOrders = (orders: Order[]) => {
    if (activeTab === 1) {
      // custom sorting for picking lists
      return getSortedOrdersForPicking(orders)
    }
    return [...orders].sort((a, b) => sortOrdersFn(a, b))
  }

  const handleChangeSearchInput = (e: any) => {
    setSearchString(e.target.value)
  }

  const getGamesInOrders = () => {
    const games: Set<string> = new Set()
    orders.forEach((o) => {
      o.articleGames.forEach((game) => {
        if (game !== 'Unknown' && game !== 'Generic') games.add(game)
      })
    })
    return Array.from(games)
  }

  const getOrderFilters = () => {
    const orderFilters: OrderFilter[] = []
    if (activeTab !== -1) {
      ORDER_TABS[activeTab].filters.forEach((filterName) => {
        const of = ORDER_FILTERS.find((f) => f.name === filterName)
        if (of) {
          orderFilters.push(of)
        }
      })
    }
    return orderFilters
  }

  const handleClickOrderFilter = (state: string) => {
    let newActiveOrdersFilters = [...activeOrdersFilters]
    if (newActiveOrdersFilters.includes(state)) {
      if (state === 'all') {
        newActiveOrdersFilters = []
      } else {
        newActiveOrdersFilters = newActiveOrdersFilters.filter((f) => f !== state)
      }
    } else {
      if (state === 'all') {
        newActiveOrdersFilters = ORDER_FILTERS.filter((f) => f.defaultActive).map((f) => f.state)
      } else {
        newActiveOrdersFilters.push(state)
      }
    }
    setActiveOrdersFilters(newActiveOrdersFilters)
  }

  const markSelectedOrdersAsPacked = async () => {
    const updatedOrders = await Promise.all(
      selectedOrders.map((order) => changeOrdersState(order.ptOrderId, 'packed'))
    )

    const newOrders = updatedOrders.map((order) => convertOrderResponseDtoToOrderClass(order))

    handleUpdateOrders(newOrders)
    setSelectedOrders([]) // Reset selezione
    setSelectedOrderIds([])
  }

  const markSelectedOrdersAsSent = async () => {
    const updatedOrders = await Promise.all(
      selectedOrders.map((order) => changeOrdersState(order.ptOrderId, 'sent'))
    )
    const newOrders = updatedOrders.map((order) => convertOrderResponseDtoToOrderClass(order))

    handleUpdateOrders(newOrders)
    setSelectedOrders([]) // Reset selezione
    setSelectedOrderIds([])
  }

  const toolNamesWidth = '70px'

  return (
    <>
      <div className="d-flex py-1 flex-row">
        <Box alignSelf="center" width={toolNamesWidth}>
          <Typography color="primary">{t('table.search')}</Typography>
        </Box>

        <Box width="70%">
          <div className="d-flex align-items-center">
            <i className="fa-light fa-magnifying-glass me-2"></i>
            <TextField
              placeholder={t('orders.searchBar')}
              fullWidth
              onChange={handleChangeSearchInput}
              variant="standard"
            />
          </div>
        </Box>

        <Box width="25%">
          {!viewOnly && (
            <Button
              variant="outlined"
              onClick={openExportOrdersDialog}
              data-testid="open-export-orders-dialog-btn"
              className="mx-3"
            >
              {t('csv.export')}
            </Button>
          )}
        </Box>
      </div>

      {activeTab === ORDER_SIDEBAR_TABS.MANAGE_PICKING_LISTS && (
        <Box display="flex" paddingY={1}>
          <PickingListToolbar
            handleUpdateOrders={handleUpdateOrders}
            toolNamesWidth={toolNamesWidth}
          />
        </Box>
      )}

      {activeTab === ORDER_SIDEBAR_TABS.PACKAGE && (
        <div className="d-flex py-1 flex-column flex-md-row align-items-md-center gap-2">
          <Typography color="primary" style={{ minWidth: toolNamesWidth }}>
            Actions
          </Typography>
          <div className="d-flex flex-wrap gap-2">
            <Button
              variant="outlined"
              disabled={disabledPackedButton}
              onClick={markSelectedOrdersAsPacked}
            >
              {t('orders.markAsPacked')}
            </Button>
            <Button
              variant="outlined"
              onClick={markSelectedOrdersAsSent}
              disabled={disabledSendButton}
            >
              {t('orders.markAsSent')}
            </Button>
          </div>
        </div>
      )}

      <div className="mt-2 d-flex flex-column flex-md-row align-items-md-center gap-2">
        <Typography color="primary" style={{ minWidth: toolNamesWidth }}>
          {t('filters.title')}
        </Typography>
        <div className="d-flex flex-wrap align-items-center">
          {getOrderFilters().map((f) =>
            activeOrdersFilters.includes(f.state) ? (
              <ColorBox
                key={f.name}
                type="filter"
                name={f.name}
                state={f.state}
                active={activeOrdersFilters.includes(f.state)}
                clickable
                onClick={() => handleClickOrderFilter(f.state)}
              />
            ) : null
          )}
          {!loadingFromMkm &&
            getGamesInOrders().map((g) =>
              activeOrdersFilters.includes(g) ? (
                <Box key={g} marginX="5px" onClick={() => handleClickOrderFilter(g)}>
                  <div
                    className="game-filter"
                    style={{ opacity: activeOrdersFilters.includes(g) ? 1 : 0.4 }}
                  >
                    <span
                      style={{ fontSize: '1.3rem' }}
                      className={`ss-${getGameCodeFromName(g)} ss-game-icon ss-fw`}
                    ></span>
                    <span>{getGameCodeFromName(g)}</span>
                  </div>
                </Box>
              ) : null
            )}
        </div>
      </div>

      <div className="mt-2 d-flex flex-column flex-md-row align-items-md-center gap-2">
        <Typography color="primary" style={{ minWidth: toolNamesWidth }}>
          {t('filters.available')}
        </Typography>
        <div className="d-flex flex-wrap align-items-center">
          {getOrderFilters().map((f) =>
            !activeOrdersFilters.includes(f.state) ? (
              <ColorBox
                key={f.name}
                type="filter"
                name={f.name}
                state={f.state}
                active={activeOrdersFilters.includes(f.state)}
                clickable
                onClick={() => handleClickOrderFilter(f.state)}
              />
            ) : null
          )}
          {!loadingFromMkm &&
            getGamesInOrders().map((g) =>
              !activeOrdersFilters.includes(g) ? (
                <Box key={g} marginX="5px" onClick={() => handleClickOrderFilter(g)}>
                  <div
                    className="game-filter"
                    style={{ opacity: activeOrdersFilters.includes(g) ? 1 : 0.4 }}
                  >
                    <span
                      style={{ fontSize: '1.3rem' }}
                      className={`ss-${getGameCodeFromName(g)} ss-game-icon ss-fw`}
                    ></span>
                    <span>{getGameCodeFromName(g)}</span>
                  </div>
                </Box>
              ) : null
            )}
        </div>
      </div>

      {view === 'article' && filterByOrder.length > 0 && (
        <Box display="flex" paddingX={1}>
          {filterByOrder.map((o) => {
            return (
              <Chip
                key={o}
                label={o}
                onDelete={() => {
                  const newFilterByOrder = [...filterByOrder]
                  const idx = newFilterByOrder.indexOf(o)
                  if (idx !== -1) {
                    newFilterByOrder.splice(idx, 1)
                    setFilterByOrder(newFilterByOrder)
                  }
                }}
                style={{ marginRight: '4px' }}
              ></Chip>
            )
          })}
        </Box>
      )}

      <ExportDialog
        title={t('csv.export')}
        content={<ExportOrders orders={orders} filteredOrders={filteredOrders} />}
      />
    </>
  )
})
